import React, { useState, useRef, useEffect } from 'react';
import './CustomSelect.css';

const CustomSelect = ({ value, onChange, options, placeholder, disabled, className }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);

  // Track whether a scroll is happening inside the dropdown.
  // If yes, suppress the outside-touch handler so the dropdown stays open.
  const isScrollingRef = useRef(false);
  const scrollTimerRef = useRef(null);

  const selectedOption = options.find(opt => String(opt.value) === String(value));

  useEffect(() => {
    const handleOutsideTouch = (e) => {
      // If touch is inside the wrapper, do nothing
      if (wrapperRef.current && wrapperRef.current.contains(e.target)) return;
      // If a scroll is in progress inside the dropdown, do not close
      if (isScrollingRef.current) return;
      setIsOpen(false);
    };

    const handleOutsideClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideTouch, { passive: true });

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideTouch);
    };
  }, []);

  // Attach scroll listener to dropdown when it opens
  useEffect(() => {
    const dropdown = dropdownRef.current;
    if (!dropdown) return;

    const handleScroll = () => {
      isScrollingRef.current = true;
      // Clear any previous timer and reset after scroll settles
      clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = setTimeout(() => {
        isScrollingRef.current = false;
      }, 150);
    };

    dropdown.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      dropdown.removeEventListener('scroll', handleScroll);
      clearTimeout(scrollTimerRef.current);
    };
  }, [isOpen]); // Re-attach whenever dropdown opens/closes

  const handleTriggerClick = () => {
    if (!disabled) setIsOpen(prev => !prev);
  };

  const handleSelect = (optionValue) => {
    onChange({ target: { value: optionValue } });
    setIsOpen(false);
  };

  return (
    <div
      className={`custom-select-wrapper ${className || ''} ${disabled ? 'disabled' : ''}`}
      ref={wrapperRef}
    >
      <div
        className={`custom-select-trigger ${isOpen ? 'open' : ''}`}
        onClick={handleTriggerClick}
      >
        <span className={selectedOption ? 'selected-text' : 'placeholder-text'}>
          {selectedOption ? selectedOption.label : placeholder}
        </span>
        <span className={`custom-select-arrow ${isOpen ? 'rotated' : ''}`}>▾</span>
      </div>

      {isOpen && (
        <div className="custom-select-dropdown" ref={dropdownRef}>
          {options.map((option) => (
            <div
              key={option.value}
              className={`custom-select-option ${String(option.value) === String(value) ? 'selected' : ''}`}
              onMouseDown={(e) => {
                e.preventDefault();
                handleSelect(option.value);
              }}
              onTouchEnd={(e) => {
                // Only fire if not scrolling
                if (!isScrollingRef.current) {
                  e.preventDefault();
                  handleSelect(option.value);
                }
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default CustomSelect;